import React, { useState, useRef, useEffect } from "react";
import { Search, X, ChevronDown, SlidersHorizontal, Check, Download, FileText } from "lucide-react";
import PersianDatePicker, { gregorianToJalaliStr } from "../../../utils/PersianDatePicker";

export interface JournalFilterConfig {
  showSearch?: boolean;
  showDateRange?: boolean;
  showAmountRange?: boolean;
  showCurrency?: boolean;
  showStatus?: boolean;
  showCounterparty?: boolean;
  showAccount?: boolean;
  showCategory?: boolean;
  showProductName?: boolean;
  counterpartyLabel?: string;
  counterpartyOptions?: { value: string; label: string }[];
  accountOptions?: { value: string; label: string }[];
  categoryOptions?: { value: string; label: string }[];
  statusOptions?: { value: string; label: string }[];
  searchPlaceholder?: string;
}

export interface JournalFilterValues {
  search: string;
  dateFrom: string;
  dateTo: string;
  amountMin: string;
  amountMax: string;
  currency: string;
  status: string;
  counterparty: string;
  account: string;
  category: string;
  productName: string;
}

export const emptyFilter: JournalFilterValues = {
  search: "", dateFrom: "", dateTo: "",
  amountMin: "", amountMax: "",
  currency: "", status: "", counterparty: "", account: "", category: "",
  productName: "",
};

const CURRENCIES = ["USD", "AFN", "IRR", "TRY", "PKR", "EUR"];

interface Props {
  config: JournalFilterConfig;
  values: JournalFilterValues;
  onChange: (v: JournalFilterValues) => void;
  resultCount: number;
  totalCount: number;
  T: any;
  dark: boolean;
  onExport?: () => void;
  exportLabel?: string;
  onReport?: () => void;
  reportLabel?: string;
  page?: number;
  totalPages?: number;
  onPageChange?: (p: number) => void;
}

// ===== Searchable Dropdown =====
function SearchableDropdown({
  options, value, onChange, placeholder, dark, bg, border, textMain, textSub, bgInput,
}: {
  options: { value: string; label: string }[];
  value: string;
  onChange: (v: string) => void;
  placeholder: string;
  dark: boolean; bg: string; border: string; textMain: string; textSub: string; bgInput: string;
}) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const h = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  const filtered = options.filter(o => o.label.toLowerCase().includes(search.toLowerCase()));
  const selected = options.find(o => o.value === value);

  return (
    <div ref={ref} style={{ position: "relative" }}>
      <div onClick={() => setOpen(o => !o)} style={{
        display: "flex", alignItems: "center", justifyContent: "space-between",
        padding: "9px 12px", borderRadius: 8,
        border: "1.5px solid " + (open ? "#8b5cf6" : border),
        background: bgInput, color: selected ? textMain : textSub,
        fontSize: 12, cursor: "pointer", transition: "all 0.2s",
        fontFamily: "'Vazirmatn','Segoe UI',sans-serif",
      }}>
        <span>{selected ? selected.label : placeholder}</span>
        <ChevronDown size={12} color={textSub} style={{ transform: open ? "rotate(180deg)" : "rotate(0)", transition: "transform 0.2s", flexShrink: 0 }} />
      </div>

      {open && (
        <div style={{
          position: "absolute", top: "calc(100% + 4px)", right: 0, left: 0, zIndex: 99999,
          background: bg, border: "1.5px solid " + border,
          borderRadius: 10, overflow: "hidden",
          boxShadow: dark ? "0 8px 24px rgba(0,0,0,0.7)" : "0 8px 24px rgba(0,0,0,0.15)",
          maxHeight: 220,
        }}>
          {/* جستجو */}
          <div style={{ padding: "8px 8px 4px", borderBottom: "1px solid " + border }}>
            <div style={{ position: "relative" }}>
              <Search size={12} style={{ position: "absolute", right: 8, top: "50%", transform: "translateY(-50%)", color: textSub, pointerEvents: "none" }} />
              <input
                autoFocus
                value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder="جستجو..."
                style={{
                  width: "100%", padding: "6px 26px 6px 8px", borderRadius: 6,
                  border: "1.5px solid " + border, background: bgInput,
                  color: textMain, fontSize: 11, outline: "none", boxSizing: "border-box",
                  fontFamily: "'Vazirmatn','Segoe UI',sans-serif",
                }}
              />
            </div>
          </div>

          {/* لیست */}
          <div style={{ overflowY: "auto", maxHeight: 160 }}>
            <div
              onClick={() => { onChange(""); setOpen(false); setSearch(""); }}
              style={{
                padding: "8px 12px", cursor: "pointer", fontSize: 11,
                color: value === "" ? "#a78bfa" : textSub,
                background: value === "" ? (dark ? "rgba(139,92,246,0.15)" : "#f5f3ff") : "transparent",
                display: "flex", alignItems: "center", justifyContent: "space-between",
              }}
            >
              <span>همه</span>
              {value === "" && <Check size={11} color="#a78bfa" />}
            </div>
            {filtered.length === 0 && (
              <div style={{ padding: "10px 12px", fontSize: 11, color: textSub, textAlign: "center" }}>نتیجه‌ای یافت نشد</div>
            )}
            {filtered.map(o => (
              <div
                key={o.value}
                onClick={() => { onChange(o.value); setOpen(false); setSearch(""); }}
                style={{
                  padding: "8px 12px", cursor: "pointer", fontSize: 11,
                  color: value === o.value ? "#a78bfa" : textMain,
                  background: value === o.value ? (dark ? "rgba(139,92,246,0.15)" : "#f5f3ff") : "transparent",
                  display: "flex", alignItems: "center", justifyContent: "space-between",
                  transition: "background 0.15s",
                }}
                onMouseEnter={e => { if (value !== o.value) (e.currentTarget as HTMLElement).style.background = dark ? "rgba(255,255,255,0.05)" : "#f8fafc"; }}
                onMouseLeave={e => { if (value !== o.value) (e.currentTarget as HTMLElement).style.background = "transparent"; }}
              >
                <span style={{ flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{o.label}</span>
                {value === o.value && <Check size={11} color="#a78bfa" style={{ flexShrink: 0, marginRight: 6 }} />}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ===== PillTag =====
function PillTag({ label, onRemove, dark }: { label: string; onRemove: () => void; dark: boolean }) {
  return (
    <span style={{
      display: "inline-flex", alignItems: "center", gap: 4,
      background: dark ? "rgba(139,92,246,0.2)" : "#ede9fe",
      color: "#a78bfa", borderRadius: 20,
      padding: "3px 10px 3px 6px",
      fontSize: 11, fontWeight: 600,
      border: "1px solid rgba(139,92,246,0.3)",
    }}>
      {label}
      <button onClick={onRemove} style={{ background: "none", border: "none", cursor: "pointer", color: "#a78bfa", padding: 0, lineHeight: 1, display: "flex", alignItems: "center" }}>
        <X size={11} />
      </button>
    </span>
  );
}

// ===== Main Component =====
export default function JournalFilter({ config, values, onChange, resultCount, totalCount, T, dark, onExport, exportLabel, onReport, reportLabel, page, totalPages, onPageChange }: Props) {
  const [open, setOpen] = useState(false);
  const panelRef = useRef<HTMLDivElement>(null);

  // رنگ‌ها کاملاً بر اساس تم
  const bg       = dark ? "#1e1a2e" : "#ffffff";
  const bgInput  = dark ? "rgba(255,255,255,0.08)" : "#f8fafc";
  const border   = dark ? "rgba(255,255,255,0.15)" : "#e2e8f0";
  const textMain = dark ? "#f0eeff" : "#1e1b2e";
  const textSub  = dark ? "rgba(200,190,255,0.55)" : "#64748b";
  const shadow   = dark ? "0 12px 40px rgba(0,0,0,0.75)" : "0 8px 32px rgba(0,0,0,0.12)";
  const panelBg  = dark ? "#1e1a2e" : "#ffffff";
  const panelBorder = dark ? "rgba(139,92,246,0.3)" : "#e2e8f0";

  const activeCount = Object.entries(values).filter(([k, v]) => k !== "search" && v !== "").length;
  const hasAny = values.search !== "" || activeCount > 0;

  useEffect(() => {
    const h = (e: MouseEvent) => { if (panelRef.current && !panelRef.current.contains(e.target as Node)) setOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  const set = (key: keyof JournalFilterValues, val: string) => onChange({ ...values, [key]: val });
  const reset = () => onChange(emptyFilter);

  const inputSt: React.CSSProperties = {
    padding: "9px 12px", borderRadius: 8,
    border: "1.5px solid " + border,
    background: bgInput, color: textMain,
    fontSize: 12, width: "100%",
    boxSizing: "border-box", outline: "none",
    fontFamily: "'Vazirmatn','Segoe UI',sans-serif",
  };

  const labelSt: React.CSSProperties = {
    fontSize: 11, color: textSub,
    marginBottom: 6, display: "block", fontWeight: 700,
  };

  // پیل‌های فعال
  const pills: React.ReactElement[] = [];
  if (values.dateFrom || values.dateTo) {
    const lbl = [values.dateFrom && gregorianToJalaliStr(values.dateFrom), values.dateTo && gregorianToJalaliStr(values.dateTo)].filter(Boolean).join(" — ");
    pills.push(<PillTag key="date" label={"📅 " + lbl} onRemove={() => onChange({ ...values, dateFrom: "", dateTo: "" })} dark={dark} />);
  }
  if (values.amountMin || values.amountMax) {
    const lbl = [values.amountMin && "از " + values.amountMin, values.amountMax && "تا " + values.amountMax].filter(Boolean).join(" ");
    pills.push(<PillTag key="amt" label={"💰 " + lbl} onRemove={() => onChange({ ...values, amountMin: "", amountMax: "" })} dark={dark} />);
  }
  if (values.currency) pills.push(<PillTag key="cur" label={"💱 " + values.currency} onRemove={() => set("currency", "")} dark={dark} />);
  if (values.status) {
    const lbl = config.statusOptions?.find(s => s.value === values.status)?.label || values.status;
    pills.push(<PillTag key="st" label={"🔖 " + lbl} onRemove={() => set("status", "")} dark={dark} />);
  }
  if (values.counterparty) {
    const lbl = config.counterpartyOptions?.find(c => c.value === values.counterparty)?.label || values.counterparty;
    pills.push(<PillTag key="cp" label={"👤 " + lbl} onRemove={() => set("counterparty", "")} dark={dark} />);
  }
  if (values.account) {
    const lbl = config.accountOptions?.find(a => a.value === values.account)?.label || values.account;
    pills.push(<PillTag key="acc" label={"🏦 " + lbl} onRemove={() => set("account", "")} dark={dark} />);
  }
  if (values.category) {
    const lbl = config.categoryOptions?.find(c => c.value === values.category)?.label || values.category;
    pills.push(<PillTag key="cat" label={"🗂 " + lbl} onRemove={() => set("category", "")} dark={dark} />);
  }
  if (values.productName) pills.push(<PillTag key="pn" label={"📦 " + values.productName} onRemove={() => set("productName", "")} dark={dark} />);

  return (
    <div style={{ marginBottom: 16, position: "relative", fontFamily: "'Vazirmatn','Segoe UI',sans-serif" }} ref={panelRef}>

      {/* نوار اصلی */}
      <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
        {/* جستجو */}
        <div style={{ flex: 1, minWidth: 220, position: "relative" }}>
          <Search size={14} style={{ position: "absolute", right: 10, top: "50%", transform: "translateY(-50%)", color: textSub, pointerEvents: "none" }} />
          <input value={values.search} onChange={e => set("search", e.target.value)}
            placeholder={config.searchPlaceholder || "جستجو..."}
            style={{ ...inputSt, paddingRight: 32 }} />
          {values.search && (
            <button onClick={() => set("search", "")} style={{ position: "absolute", left: 8, top: "50%", transform: "translateY(-50%)", background: "none", border: "none", cursor: "pointer", color: textSub, padding: 0 }}>
              <X size={13} />
            </button>
          )}
        </div>

        {/* دکمه فیلتر پیشرفته */}
        <button onClick={() => setOpen(o => !o)} style={{
          display: "flex", alignItems: "center", gap: 6,
          padding: "9px 14px", borderRadius: 8, cursor: "pointer", fontSize: 12, fontWeight: 700,
          border: "1.5px solid " + (activeCount > 0 ? "#8b5cf6" : border),
          background: activeCount > 0 ? (dark ? "rgba(139,92,246,0.2)" : "#f5f3ff") : bgInput,
          color: activeCount > 0 ? "#a78bfa" : textMain,
          transition: "all 0.2s",
          fontFamily: "'Vazirmatn','Segoe UI',sans-serif",
        }}>
          <SlidersHorizontal size={14} />
          فیلتر پیشرفته
          {activeCount > 0 && (
            <span style={{ background: "#8b5cf6", color: "#fff", borderRadius: "50%", width: 18, height: 18, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 10, fontWeight: 800 }}>
              {activeCount}
            </span>
          )}
          <ChevronDown size={12} style={{ transform: open ? "rotate(180deg)" : "rotate(0)", transition: "transform 0.2s" }} />
        </button>

        <span style={{ fontSize: 11, color: hasAny && resultCount !== totalCount ? "#a78bfa" : textSub, whiteSpace: "nowrap" }}>
          {hasAny ? resultCount + " از " + totalCount + " نتیجه" : totalCount + " رکورد"}
        </span>

        {hasAny && (
          <button onClick={reset} style={{ display: "flex", alignItems: "center", gap: 4, padding: "6px 10px", borderRadius: 8, cursor: "pointer", fontSize: 11, border: "1.5px solid #ef4444", background: "transparent", color: "#ef4444", fontFamily: "'Vazirmatn','Segoe UI',sans-serif" }}>
            <X size={12} /> پاک کردن
          </button>
        )}

        {onReport && (
          <button onClick={onReport} style={{ display: "flex", alignItems: "center", gap: 6, padding: "6px 16px", borderRadius: 8, cursor: "pointer", fontSize: 12, fontWeight: 800, border: "none", background: "linear-gradient(120deg,#10b981,#059669)", color: "#fff", fontFamily: "'Vazirmatn','Segoe UI',sans-serif", whiteSpace: "nowrap", boxShadow: "0 3px 10px rgba(16,185,129,0.3)" }}>
            <FileText size={13} /> {reportLabel || "گزارش PDF / Excel"}
          </button>
        )}
        {onExport && !onReport && (
          <button onClick={onExport} style={{ display: "flex", alignItems: "center", gap: 6, padding: "6px 14px", borderRadius: 8, cursor: "pointer", fontSize: 12, fontWeight: 700, border: "1.5px solid #10b981", background: "transparent", color: "#10b981", fontFamily: "'Vazirmatn','Segoe UI',sans-serif", whiteSpace: "nowrap" }}>
            <Download size={13} /> {exportLabel || "دانلود Excel"}
          </button>
        )}
      </div>

      {/* پیل‌های فعال */}
      {pills.length > 0 && <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginTop: 8 }}>{pills}</div>}

      {/* پنل فیلتر پیشرفته */}
      {open && (
        <div style={{
          position: "absolute", top: "calc(100% + 8px)", right: 0, zIndex: 9999,
          background: panelBg,
          border: "1.5px solid " + panelBorder,
          borderRadius: 14, padding: 20,
          boxShadow: shadow,
          minWidth: 340, maxWidth: 500, width: "100%",
        }}>
          {/* هدر پنل */}
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 18 }}>
            <strong style={{ fontSize: 13, color: textMain, display: "flex", alignItems: "center", gap: 6 }}>
              <SlidersHorizontal size={14} color="#8b5cf6" /> فیلترهای پیشرفته
            </strong>
            <button onClick={() => setOpen(false)} style={{ background: "none", border: "none", cursor: "pointer", color: textSub, display: "flex", alignItems: "center" }}>
              <X size={16} />
            </button>
          </div>

          <div style={{ display: "grid", gap: 16 }}>

            {/* بازه تاریخ */}
            {config.showDateRange !== false && (
              <div>
                <label style={labelSt}>📅 بازه تاریخ</label>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
                  <div><div style={{ fontSize: 10, color: textSub, marginBottom: 3 }}>از تاریخ</div><PersianDatePicker value={values.dateFrom} onChange={v => set("dateFrom", v)} /></div>
                  <div><div style={{ fontSize: 10, color: textSub, marginBottom: 3 }}>تا تاریخ</div><PersianDatePicker value={values.dateTo} onChange={v => set("dateTo", v)} /></div>
                </div>
              </div>
            )}

            {/* بازه مبلغ */}
            {config.showAmountRange !== false && (
              <div>
                <label style={labelSt}>💰 بازه مبلغ</label>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
                  <div><div style={{ fontSize: 10, color: textSub, marginBottom: 3 }}>حداقل</div><input type="number" value={values.amountMin} onChange={e => set("amountMin", e.target.value)} placeholder="0" style={inputSt} /></div>
                  <div><div style={{ fontSize: 10, color: textSub, marginBottom: 3 }}>حداکثر</div><input type="number" value={values.amountMax} onChange={e => set("amountMax", e.target.value)} placeholder="∞" style={inputSt} /></div>
                </div>
              </div>
            )}

            {/* ارز */}
            {config.showCurrency !== false && (
              <div>
                <label style={labelSt}>💱 ارز</label>
                <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                  {["", ...CURRENCIES].map(c => (
                    <button key={c} onClick={() => set("currency", c)} style={{
                      padding: "5px 12px", borderRadius: 20, fontSize: 11, fontWeight: 600, cursor: "pointer",
                      border: "1.5px solid " + (values.currency === c ? "#8b5cf6" : border),
                      background: values.currency === c ? (dark ? "rgba(139,92,246,0.25)" : "#f5f3ff") : "transparent",
                      color: values.currency === c ? "#a78bfa" : textSub,
                      fontFamily: "'Vazirmatn','Segoe UI',sans-serif",
                    }}>{c || "همه"}</button>
                  ))}
                </div>
              </div>
            )}

            {/* وضعیت */}
            {config.showStatus && config.statusOptions && (
              <div>
                <label style={labelSt}>🔖 وضعیت</label>
                <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                  {[{ value: "", label: "همه" }, ...config.statusOptions].map(s => (
                    <button key={s.value} onClick={() => set("status", s.value)} style={{
                      padding: "5px 12px", borderRadius: 20, fontSize: 11, fontWeight: 600, cursor: "pointer",
                      border: "1.5px solid " + (values.status === s.value ? "#8b5cf6" : border),
                      background: values.status === s.value ? (dark ? "rgba(139,92,246,0.25)" : "#f5f3ff") : "transparent",
                      color: values.status === s.value ? "#a78bfa" : textSub,
                      fontFamily: "'Vazirmatn','Segoe UI',sans-serif",
                    }}>{s.label}</button>
                  ))}
                </div>
              </div>
            )}

            {/* طرف حساب - با جستجو */}
            {config.showCounterparty && config.counterpartyOptions && (
              <div>
                <label style={labelSt}>👤 {config.counterpartyLabel || "طرف حساب"}</label>
                <SearchableDropdown
                  options={config.counterpartyOptions}
                  value={values.counterparty}
                  onChange={v => set("counterparty", v)}
                  placeholder={"انتخاب " + (config.counterpartyLabel || "طرف حساب")}
                  dark={dark} bg={panelBg} border={border} textMain={textMain} textSub={textSub} bgInput={bgInput}
                />
              </div>
            )}

            {/* نام جنس */}
            {config.showProductName !== false && (
              <div>
                <label style={labelSt}>📦 نام جنس / محصول</label>
                <div style={{ position: "relative" }}>
                  <Search size={13} style={{ position: "absolute", right: 10, top: "50%", transform: "translateY(-50%)", color: textSub, pointerEvents: "none" }} />
                  <input value={values.productName} onChange={e => set("productName", e.target.value)}
                    placeholder="جستجو بر اساس نام جنس..." style={{ ...inputSt, paddingRight: 30 }} />
                </div>
              </div>
            )}

            {/* حساب - با جستجو */}
            {config.showAccount && config.accountOptions && (
              <div>
                <label style={labelSt}>🏦 حساب</label>
                <SearchableDropdown
                  options={config.accountOptions}
                  value={values.account}
                  onChange={v => set("account", v)}
                  placeholder="انتخاب حساب"
                  dark={dark} bg={panelBg} border={border} textMain={textMain} textSub={textSub} bgInput={bgInput}
                />
              </div>
            )}

            {/* دسته‌بندی - با جستجو */}
            {config.showCategory && config.categoryOptions && (
              <div>
                <label style={labelSt}>🗂 دسته‌بندی</label>
                <SearchableDropdown
                  options={config.categoryOptions}
                  value={values.category}
                  onChange={v => set("category", v)}
                  placeholder="انتخاب دسته‌بندی"
                  dark={dark} bg={panelBg} border={border} textMain={textMain} textSub={textSub} bgInput={bgInput}
                />
              </div>
            )}

          </div>

          {/* دکمه‌های پایین */}
          <div style={{ display: "flex", gap: 8, marginTop: 18, justifyContent: "flex-end" }}>
            {activeCount > 0 && (
              <button onClick={reset} style={{ padding: "8px 16px", borderRadius: 8, fontSize: 12, cursor: "pointer", fontWeight: 600, border: "1.5px solid #ef4444", background: "transparent", color: "#ef4444", fontFamily: "'Vazirmatn','Segoe UI',sans-serif" }}>
                پاک کردن همه
              </button>
            )}
            <button onClick={() => setOpen(false)} style={{ padding: "8px 22px", borderRadius: 8, fontSize: 12, cursor: "pointer", fontWeight: 700, border: "none", background: "linear-gradient(135deg,#8b5cf6,#7c3aed)", color: "#fff", fontFamily: "'Vazirmatn','Segoe UI',sans-serif" }}>
              اعمال فیلتر
            </button>
          </div>
        </div>
      )}

      {typeof page === "number" && typeof totalPages === "number" && totalPages > 1 && (
        <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 10, marginTop: 12 }}>
          <button
            onClick={() => onPageChange && onPageChange(Math.max(1, page - 1))}
            disabled={page <= 1}
            style={{
              padding: "7px 14px", borderRadius: 8, border: "1.5px solid " + border,
              background: bgInput, color: page <= 1 ? textSub : textMain,
              cursor: page <= 1 ? "not-allowed" : "pointer", fontSize: 12, fontWeight: 700,
              fontFamily: "'Vazirmatn','Segoe UI',sans-serif", opacity: page <= 1 ? 0.5 : 1,
            }}
          >
            ‹ قبلی
          </button>
          <span style={{ fontSize: 12, color: textSub, fontWeight: 700 }}>
            صفحه {page} از {totalPages}
          </span>
          <button
            onClick={() => onPageChange && onPageChange(Math.min(totalPages, page + 1))}
            disabled={page >= totalPages}
            style={{
              padding: "7px 14px", borderRadius: 8, border: "1.5px solid " + border,
              background: bgInput, color: page >= totalPages ? textSub : textMain,
              cursor: page >= totalPages ? "not-allowed" : "pointer", fontSize: 12, fontWeight: 700,
              fontFamily: "'Vazirmatn','Segoe UI',sans-serif", opacity: page >= totalPages ? 0.5 : 1,
            }}
          >
            بعدی ›
          </button>
        </div>
      )}
    </div>
  );
}

export function applyFilter<T extends Record<string, any>>(
  list: T[],
  values: JournalFilterValues,
  opts: {
    searchFields?: (keyof T)[];
    dateField?: keyof T;
    amountField?: keyof T;
    currencyField?: keyof T;
    statusField?: keyof T;
    counterpartyField?: keyof T;
    accountField?: keyof T;
    categoryField?: keyof T;
    productNameField?: keyof T;
  }
): T[] {
  return list.filter(item => {
    if (values.search) {
      const q = values.search.toLowerCase();
      // 🆕 truck_plate همیشه در جستجو شامل می‌شود (اگر در رکورد موجود باشد)
      const allSearchFields: string[] = [
        ...(opts.searchFields || []).map(f => String(f)),
        "truck_plate",
      ];
      const match = allSearchFields.some(f => {
        const v = (item as any)[f];
        return v && String(v).toLowerCase().includes(q);
      });
      if (!match) return false;
    }
    if (values.dateFrom && opts.dateField && String(item[opts.dateField]) < values.dateFrom) return false;
    if (values.dateTo && opts.dateField && String(item[opts.dateField]) > values.dateTo) return false;
    if (values.amountMin && opts.amountField && Number(item[opts.amountField]) < Number(values.amountMin)) return false;
    if (values.amountMax && opts.amountField && Number(item[opts.amountField]) > Number(values.amountMax)) return false;
    if (values.currency && opts.currencyField && item[opts.currencyField] !== values.currency) return false;
    if (values.status && opts.statusField && item[opts.statusField] !== values.status) return false;
    if (values.counterparty && opts.counterpartyField && String(item[opts.counterpartyField]) !== values.counterparty) return false;
    if (values.account && opts.accountField && String(item[opts.accountField]) !== values.account) return false;
    if (values.category && opts.categoryField && String(item[opts.categoryField]) !== values.category) return false;
    if (values.productName) {
      const q = values.productName.toLowerCase();
      if (opts.productNameField) {
        const v = item[opts.productNameField];
        if (!v || !String(v).toLowerCase().includes(q)) return false;
      } else {
        const items = item["items"] as any[];
        if (items && items.length > 0) {
          if (!items.some((it: any) => it.product_name && it.product_name.toLowerCase().includes(q))) return false;
        }
      }
    }
    return true;
  });
}

// ===== تابع Export به Excel =====
export function exportToExcel(rows: Record<string, any>[], filename: string) {
  if (!rows || rows.length === 0) return;

  const escapeXml = (v: any): string => {
    if (v === null || v === undefined) return "";
    return String(v).replace(/&/g,"&amp;").replace(/</g,"&lt;").replace(/>/g,"&gt;").replace(/"/g,"&quot;");
  };

  const getType = (v: any): "Number" | "String" => {
    if (v === null || v === undefined || String(v).trim() === "") return "String";
    const s = String(v).trim().replace(/,/g, "");
    return (s !== "" && !isNaN(Number(s))) ? "Number" : "String";
  };

  const headers = Object.keys(rows[0]);
  const headerRow = headers.map(h =>
    `<Cell ss:StyleID="header"><Data ss:Type="String">${escapeXml(h)}</Data></Cell>`
  ).join("");

  const dataRows = rows.map(row =>
    "<Row>" + headers.map(h => {
      const v = row[h];
      const t = getType(v);
      const val = t === "Number" ? String(v).trim().replace(/,/g, "") : escapeXml(v);
      return `<Cell><Data ss:Type="${t}">${val}</Data></Cell>`;
    }).join("") + "</Row>"
  ).join("\n");

  const xml = `<?xml version="1.0" encoding="UTF-8"?>
<?mso-application progid="Excel.Sheet"?>
<Workbook xmlns="urn:schemas-microsoft-com:office:spreadsheet"
 xmlns:ss="urn:schemas-microsoft-com:office:spreadsheet">
 <Styles>
  <Style ss:ID="header">
   <Font ss:Bold="1" ss:Color="#FFFFFF"/>
   <Interior ss:Color="#10b981" ss:Pattern="Solid"/>
   <Alignment ss:Horizontal="Center"/>
  </Style>
 </Styles>
 <Worksheet ss:Name="Sheet1">
  <Table>
   <Row>${headerRow}</Row>
   ${dataRows}
  </Table>
 </Worksheet>
</Workbook>`;

  const blob = new Blob([xml], { type: "application/vnd.ms-excel;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = filename + ".xls";
  document.body.appendChild(a); a.click();
  document.body.removeChild(a); URL.revokeObjectURL(url);
}